import { useCallback, useEffect, useState } from 'react';
import { Clock, ScrollText } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { FlowHistoryPanel } from '@/components/creationFlows/FlowHistoryPanel';
import {
  formatScriptAudioAlignDate,
  scriptAudioAlignStatusLabel,
} from '@/lib/scriptAudioAlignHistory';

export function ScriptAudioAlignHistoryPanel({
  open,
  onToggle,
  activeStoryId = null,
  activeJobId = null,
  onSelectJob,
  onNewProject,
  refreshKey = 0,
  loadingJobId = null,
}) {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/script-audio-align/jobs');
      setJobs(Array.isArray(data?.jobs) ? data.jobs : []);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  return (
    <FlowHistoryPanel
      open={open}
      onToggle={onToggle}
      title="Script align history"
      description="Past script-to-audio jobs in your library. Click one to reload its script, timings, and visuals."
      icon={ScrollText}
      variant="dark"
      loading={loading}
      count={jobs.length}
      onRefresh={load}
      refreshLoading={loading}
      onNew={onNewProject}
      newLabel="New alignment"
      emptyMessage="No jobs yet. Run the alignment pipeline to save one here."
      collapsedLabel="History"
      testId="script-audio-align-history-panel"
      footer={`${jobs.length} job${jobs.length === 1 ? '' : 's'}`}
    >
      <ul className="space-y-1">
        {jobs.map((job) => {
          const active = job.job_id === activeJobId || (activeStoryId && job.story_id === activeStoryId);
          const status = scriptAudioAlignStatusLabel(job);
          return (
            <li key={job.job_id}>
              <button
                type="button"
                onClick={() => onSelectJob(job)}
                disabled={loadingJobId === job.job_id}
                className={cn(
                  'w-full text-left rounded-sm px-2.5 py-2 border transition-colors disabled:opacity-60',
                  active
                    ? 'bg-violet-500/15 border-violet-500/40'
                    : 'border-transparent hover:bg-white/5 hover:border-slate-800',
                )}
                data-testid={`script-audio-align-history-item-${job.job_id}`}
              >
                <p className={cn('text-sm font-medium line-clamp-1', active ? 'text-violet-100' : 'text-slate-200')}>
                  {job.title || 'Untitled script'}
                </p>
                {job.story_id ? (
                  <p className="text-[10px] font-mono text-slate-500 line-clamp-1 mt-0.5">{job.story_id}</p>
                ) : null}
                <div className="flex items-center justify-between gap-2 mt-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">{status}</span>
                  {job.sentence_count > 0 ? (
                    <span className="text-[10px] text-slate-500">{job.sentence_count} sentences</span>
                  ) : null}
                </div>
                <span className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
                  <Clock className="w-3 h-3" />
                  {formatScriptAudioAlignDate(job.updated_at || job.created_at)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </FlowHistoryPanel>
  );
}
